import { AppTheme } from './services/theme';

export const DEFAULT_BASE_THEME: AppTheme = 'light' as const;
export const PAYMENT_REMINDER_TEMPLATE_ID = '1234567890';
export const YOUTUBE_THUMBNAIL_PLACEHOLDER = 'https://via.placeholder.com/400x225/cccccc/666666?text=Video+Thumbnail';
export const DEFAULT_PROFILE_IMAGE = 'https://picsum.photos/200';
export const DEFAULT_COURSE_THUMBNAIL = '/assets/images/course-default-thumbnail.png';
export const CERTIFICATE_IMAGE_URL = '/assets/sample-certificate-preview.png';

export const DEFAULT_ENROLL_NOW_DETAILS = {
    title: 'Step In, Learn, and Grow – Start Here',
    steps: [
        {
            number: 1,
            title: 'Apply with Ease',
            description: 'Fill out a short form with your details and upload the necessary documents.',
            icon: 'target',
        },
        {
            number: 2,
            title: 'Application Review',
            description: 'Our team carefully reviews your application to ensure the best fit for the course.',
            icon: 'file-magnify',
        },
        {
            number: 3,
            title: 'Secure Your Spot',
            description: 'Once approved, complete your enrollment by making the course payment.',
            icon: 'sheild-check',
        },
        {
            number: 4,
            title: "...And You're In!",
            description:
                'After the payment, you will be officially registered and ready to start your learning journey!',
            icon: 'hand-piece',
        },
    ],
};

export const CORPORATE_ENROLL_NOW_DETAILS = {
    title: 'Empower Your Workforce with Expert-Led Training',
    paras: [
        'Future-proof your organisation with IITB’s in-house courses, designed to enhance skills, boost innovation and drive business success.',
        'Trusted by 50+ leading companies, these programs deliver real impact.',
    ],
    steps: [
        {
            heading: 'Tailored training',
            content: 'to meet your organisation’s needs',
        },
        {
            heading: 'Industry-leading',
            content: 'curriculum designed by IITB experts',
        },
        {
            heading: 'Proven results',
            content: '—organisations higher efficiency & innovation',
        },
    ],
    footer: 'Ready to upgrade your team’s skills?',
};

export enum EnrollmentStatus {
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    REFUND = 'REFUND',
    REGISTERED = 'REGISTERED',
    ONGOING = 'ONGOING',
    PAYMENT_PROCESSING = 'PAYMENT_PROCESSING',
    COMPLETED = 'COMPLETED',
    PAYMENT_FAILED = 'PAYMENT_FAILED',
    IN_REVIEW = 'IN_REVIEW',
    REVIEW = 'REVIEW',
    MISSED = 'MISSED',
    PENDING = 'PENDING',
    SUSPENDED = 'SUSPENDED',
    ON_HOLD = 'ON_HOLD',
    CANCELLED = 'CANCELLED',
    REFUND_ISSUED = 'REFUND_ISSUED',
}

export const ENROLLMENT_STATUS_MAP: Record<EnrollmentStatus, string> = {
    [EnrollmentStatus.APPROVED]: 'Approved',
    [EnrollmentStatus.REJECTED]: 'Rejected',
    [EnrollmentStatus.REFUND]: 'Refund',
    [EnrollmentStatus.REGISTERED]: 'Registered',
    [EnrollmentStatus.ONGOING]: 'Ongoing',
    [EnrollmentStatus.PAYMENT_PROCESSING]: 'Payment Processing',
    [EnrollmentStatus.COMPLETED]: 'Completed',
    [EnrollmentStatus.PAYMENT_FAILED]: 'Payment Failed',
    [EnrollmentStatus.IN_REVIEW]: 'In Review',
    [EnrollmentStatus.REVIEW]: 'In Review',
    [EnrollmentStatus.MISSED]: 'Missed',
    [EnrollmentStatus.PENDING]: 'Pending',
    [EnrollmentStatus.SUSPENDED]: 'Suspended',
    [EnrollmentStatus.ON_HOLD]: 'On Hold',
    [EnrollmentStatus.CANCELLED]: 'Cancelled',
    [EnrollmentStatus.REFUND_ISSUED]: 'Refund Issued',
};

export enum PaymentType {
    SELF = 'SELF',
    COMPANY = 'COMPANY',
}

export const PAYMENT_TYPES: Record<PaymentType, string> = {
    [PaymentType.SELF]: 'Self',
    [PaymentType.COMPANY]: 'Corporate',
};

export const getStatusClass = (status: EnrollmentStatus | string): string => {
    switch (status) {
        case EnrollmentStatus.REGISTERED:
            return 'status-registered';
        case EnrollmentStatus.IN_REVIEW:
            return 'status-in-review';
        case EnrollmentStatus.PENDING:
            return 'status-in-review';
        case EnrollmentStatus.REVIEW:
            return 'status-in-review';
        case EnrollmentStatus.APPROVED:
            return 'status-approved';
        case EnrollmentStatus.ONGOING:
            return 'status-ongoing';
        case EnrollmentStatus.PAYMENT_PROCESSING:
            return 'status-payment-processing';
        case EnrollmentStatus.REFUND:
            return 'status-refund-initiated';
        case EnrollmentStatus.REFUND_ISSUED:
            return 'status-refund-issued';
        case EnrollmentStatus.REJECTED:
            return 'status-rejected';
        case EnrollmentStatus.COMPLETED:
            return 'status-completed';
        case EnrollmentStatus.SUSPENDED:
            return 'status-suspended';
        case EnrollmentStatus.ON_HOLD:
            return 'status-on-hold';
        case EnrollmentStatus.CANCELLED:
            return 'status-cancelled';
        default:
            return 'status-registered';
    }
};

export const convertToMonths = (startDate: string, endDate: string): string => {
    if (!startDate || !endDate) {
        return 'N/A';
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());

    const totalDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    // If less than 31 days, show only days
    if (totalDays < 31) {
        return totalDays === 1 ? '1 day' : `${totalDays} days`;
    }

    // Calculate months and remaining days
    const months = Math.floor(totalDays / 30.44);
    const remainingDays = Math.round(totalDays % 30.44);

    // Build the duration string
    const monthsText = months === 1 ? '1 month' : `${months} months`;

    if (remainingDays === 0 || remainingDays < 15) {
        return monthsText;
    } else if (remainingDays === 1) {
        return `${monthsText} 1 day`;
    } else {
        return `${monthsText} ${remainingDays} days`;
    }
};
